import { Badge } from '@/components/ui/badge'
import { User, Mail, Key, Clock, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SubjectDetailData {
  readonly id: string
  readonly primaryIdentifier: string
  readonly identifierType: string
  readonly displayName?: string
  readonly email?: string
  readonly status: string
  readonly externalId?: string
  readonly sourceIdp?: {
    readonly name: string
    readonly providerType: string
  }
  readonly lastSyncedAt?: string
  readonly createdAt: string
}

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50',
  INACTIVE: 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50',
  SUSPENDED: 'border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50',
}

export function SubjectDetailCard({ data }: { data: { message: string; subject: SubjectDetailData } }) {
  const { subject } = data
  const statusStyle = STATUS_STYLES[subject.status] ?? STATUS_STYLES.INACTIVE

  return (
    <div className="my-2 rounded-lg border border-border dark:border-border/40 bg-card shadow-sm overflow-hidden border-l-[3px] border-l-cyan-500 dark:border-l-cyan-400">
      {/* Header */}
      <div className="flex items-center gap-3 px-3.5 py-3">
        <div className="flex items-center justify-center size-8 rounded-lg shrink-0 bg-cyan-100 dark:bg-cyan-900/50">
          <User className="size-4 text-cyan-600 dark:text-cyan-400" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground">
              {subject.displayName ?? subject.primaryIdentifier}
            </span>
            <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 shrink-0 uppercase font-semibold', statusStyle)}>
              {subject.status}
            </Badge>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="px-3.5 pb-3 space-y-1.5">
        <DetailRow icon={Key} label="Identifier" value={subject.primaryIdentifier} subValue={subject.identifierType} />
        {subject.email && <DetailRow icon={Mail} label="Email" value={subject.email} />}
        {subject.externalId && <DetailRow icon={Globe} label="External ID" value={subject.externalId} />}
        {subject.sourceIdp && (
          <DetailRow icon={Globe} label="IdP Source" value={subject.sourceIdp.name} subValue={subject.sourceIdp.providerType} />
        )}
        {subject.lastSyncedAt && (
          <DetailRow icon={Clock} label="Last Synced" value={new Date(subject.lastSyncedAt).toLocaleString()} />
        )}
        <DetailRow icon={Clock} label="Created" value={new Date(subject.createdAt).toLocaleString()} />
      </div>
    </div>
  )
}

function DetailRow({
  icon: Icon,
  label,
  value,
  subValue,
}: {
  icon: typeof User
  label: string
  value: string
  subValue?: string
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className="size-3 text-muted-foreground shrink-0" strokeWidth={1.5} />
      <span className="text-muted-foreground shrink-0 w-20">{label}</span>
      <span className="text-foreground font-mono truncate">{value}</span>
      {subValue && (
        <span className="text-[10px] text-muted-foreground font-mono">({subValue})</span>
      )}
    </div>
  )
}
